// ============================================================================
// MEMBER INVITES (unified invite model)
// Owners create member_invites; invitee accepts via accept_member_invite.
// ============================================================================

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type MemberInviteRole = "company_admin" | "cleaner" | "cohost";

/** Cohost-style access: full (config + delegate) or limited (view + delegate + invite only). */
export type PropertyAccessLevel = "full" | "limited";

export interface CreateMemberInviteParams {
  ownerId: string;
  email: string;
  inviteRole: MemberInviteRole;
  propertyId?: string | null;
  companyId?: string | null;
  expiresDays?: number;
  /** Intended access_level for this invite (default: limited). */
  accessLevel?: PropertyAccessLevel | null;
  /** Slice 22: module permissions for Team invites. */
  permissions?: string[] | null;
  /** Team "Select properties": grant only these property IDs on accept. */
  propertyIds?: string[] | null;
}

export interface MemberInviteRow {
  id: string;
  owner_id: string;
  email: string;
  invite_role: MemberInviteRole;
  company_id: string | null;
  property_id: string | null;
  token: string;
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  created_by: string | null;
  created_at: string;
  access_level: PropertyAccessLevel | null;
  permissions?: unknown;
  /** When set, Team invite is scoped to these properties (not all). */
  target_property_ids?: string[] | null;
}

/** Status for display: Accepted | Revoked | Pending */
export function memberInviteStatus(inv: { accepted_at: string | null; revoked_at?: string | null; expires_at: string }): "accepted" | "revoked" | "pending" {
  if (inv.revoked_at) return "revoked";
  if (inv.accepted_at) return "accepted";
  try {
    if (new Date(inv.expires_at) <= new Date()) return "revoked";
  } catch (_) {}
  return "pending";
}

/** True when delete_revoked_member_invite will accept this row. */
export function canRemoveInviteFromList(inv: {
  accepted_at: string | null;
  revoked_at?: string | null;
  expires_at: string;
}): boolean {
  if (inv.revoked_at) return true;
  if (inv.accepted_at) return false;
  try {
    return new Date(inv.expires_at) <= new Date();
  } catch (_) {
    return false;
  }
}

export interface CreateMemberInviteResult {
  id: string;
  token: string;
  email: string;
  invite_role: MemberInviteRole;
  company_id: string | null;
  property_id: string | null;
  expires_at: string;
  created_at: string;
  access_level?: PropertyAccessLevel | null;
  permissions?: unknown;
}

export interface InviteEmailProperty {
  name: string;
  photo_url?: string | null;
}

export interface SendMemberInviteEmailParams {
  email: string;
  token: string;
  invite_role: "company_admin" | "cleaner";
  owner_name: string;
  property_name?: string;
  /** @deprecated prefer `properties` with thumbnails */
  property_names?: string[];
  /** Properties shown in the invite email (name + optional thumbnail). */
  properties?: InviteEmailProperty[];
  property_id?: string;
  permission_label: string;
}

/** Body sent to send-member-invite (camelCase per spec). */
export interface SendMemberInviteBody {
  email: string;
  token: string;
  inviteRole: "company_admin" | "cleaner";
  permission: string;
  propertyName: string;
  propertyNames?: string[];
  properties?: InviteEmailProperty[];
  inviterName: string;
}

export interface SendMemberInviteEmailResult {
  ok: boolean;
  error?: string;
}

/**
 * Call the send-member-invite edge function to email the invite link.
 * Call this after create_member_invite returns. On failure, show copy-link fallback + toast.
 */
export async function sendMemberInviteEmail(
  params: SendMemberInviteEmailParams
): Promise<SendMemberInviteEmailResult> {
  const properties: InviteEmailProperty[] =
    params.properties
      ?.map((p) => ({
        name: (p.name || "").trim(),
        photo_url: p.photo_url?.trim() || null,
      }))
      .filter((p) => p.name) ??
    (params.property_names?.map((n) => n.trim()).filter(Boolean).map((name) => ({ name, photo_url: null })) ??
      (params.property_name?.trim()
        ? [{ name: params.property_name.trim(), photo_url: null }]
        : []));
  const names = properties.map((p) => p.name);
  const body: SendMemberInviteBody = {
    email: params.email,
    token: params.token,
    inviteRole: params.invite_role,
    permission: params.permission_label ?? "",
    propertyName:
      names.length === 1 ? names[0]! : names.length > 1 ? `${names.length} properties` : params.property_name ?? "",
    propertyNames: names.length > 0 ? names : undefined,
    properties: properties.length > 0 ? properties : undefined,
    inviterName: params.owner_name ?? "",
  };
  const { data, error } = await supabase.functions.invoke("send-member-invite", { body });
  if (error) {
    console.error("[sendMemberInviteEmail] invoke error", { error: error.message, email: params.email });
    return { ok: false, error: error.message };
  }
  const result = data as { ok?: boolean; error?: string } | null;
  if (result?.ok) return { ok: true };
  const errMsg = result?.error ?? "Failed to send email";
  console.error("[sendMemberInviteEmail] function returned failure", { error: errMsg, email: params.email });
  return { ok: false, error: errMsg };
}

export interface SendAccessChangedEmailParams {
  recipientEmail: string;
  propertyId: string;
  propertyName: string;
  inviteRole: "company_admin" | "cleaner";
  permissionLevel: "full" | "limited";
  changedByNameOrEmail: string;
  effectiveAt: Date;
  recipientName?: string | null;
}

/**
 * Call the send-access-changed edge function to notify the user their access was updated.
 * Only call after DB update succeeds and permission actually changed.
 */
export async function sendAccessChangedEmail(
  params: SendAccessChangedEmailParams
): Promise<{ ok: boolean; error?: string }> {
  const baseUrl =
    typeof window !== "undefined"
      ? window.location.origin
      : (import.meta.env?.VITE_APP_URL ?? "https://portal.stravo.ai");
  const roleLabel = params.inviteRole === "company_admin" ? "Team admin" : "Cleaner";
  const permissionLabel =
    params.inviteRole === "company_admin"
      ? params.permissionLevel === "full"
        ? "Full access"
        : "Limited access"
      : params.permissionLevel === "full"
        ? "Worker"
        : "Viewer";
  const ctaUrl =
    params.inviteRole === "company_admin"
      ? `${baseUrl.replace(/\/$/, "")}/properties/${params.propertyId}`
      : `${baseUrl.replace(/\/$/, "")}/worker/schedule?property_id=${params.propertyId}`;
  const effectiveAtStr = params.effectiveAt.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

  const body = {
    recipientEmail: params.recipientEmail.trim().toLowerCase(),
    recipientName: params.recipientName ?? null,
    propertyId: params.propertyId,
    propertyName: params.propertyName || "this property",
    roleLabel,
    permissionLabel,
    changedByNameOrEmail: params.changedByNameOrEmail || "Someone",
    effectiveAt: effectiveAtStr,
    ctaUrl,
  };

  const { data, error } = await supabase.functions.invoke("send-access-changed", { body });
  if (error) {
    console.error("[sendAccessChangedEmail] invoke error", { error: error.message, to: params.recipientEmail });
    return { ok: false, error: error.message };
  }
  const result = data as { ok?: boolean; error?: string } | null;
  if (result?.ok) return { ok: true };
  const errMsg = result?.error ?? "Failed to send email";
  console.error("[sendAccessChangedEmail] function returned failure", { error: errMsg, to: params.recipientEmail });
  return { ok: false, error: errMsg };
}

/**
 * Clear expired / revoked-but-still-blocking invite rows so create_member_invite can succeed.
 * Server historically blocked on any accepted_at row even after Remove set revoked_at.
 */
async function prepareMemberInviteSlot(params: {
  ownerId: string;
  email: string;
  propertyId?: string | null;
}): Promise<void> {
  const email = params.email.trim().toLowerCase();
  if (!email) return;

  // If they still have live access, prefer a clear error before mutating invites
  if (params.propertyId) {
    const { data: accessBundle } = await supabase.rpc("get_property_access_and_invites", {
      p_property_id: params.propertyId,
    });
    const members = (accessBundle as { members?: Array<{ grantee_email?: string | null }> } | null)
      ?.members;
    if (
      Array.isArray(members) &&
      members.some((m) => (m.grantee_email || "").trim().toLowerCase() === email)
    ) {
      throw new Error("This email already has access. Remove them from Team first, then invite again.");
    }

    const { data: cohostMembers } = await supabase.rpc("get_property_cohost_members", {
      p_property_id: params.propertyId,
    });
    if (
      Array.isArray(cohostMembers) &&
      (cohostMembers as Array<{ grantee_email?: string | null }>).some(
        (m) => (m.grantee_email || "").trim().toLowerCase() === email,
      )
    ) {
      throw new Error("This email already has access. Remove them from Team first, then invite again.");
    }
  }

  const { data: invites, error } = await supabase
    .from("member_invites")
    .select("id, property_id, accepted_at, revoked_at, expires_at")
    .eq("owner_id", params.ownerId)
    .eq("email", email);

  if (error) {
    console.warn("[prepareMemberInviteSlot] list failed", error.message);
    return;
  }

  const now = Date.now();
  const isoNow = new Date().toISOString();

  for (const inv of invites ?? []) {
    const expired = inv.expires_at ? Date.parse(inv.expires_at) <= now : false;
    const sameProperty =
      !params.propertyId || !inv.property_id || inv.property_id === params.propertyId;

    // Active pending invite for this property → leave it (create will error with a clear message)
    if (!inv.accepted_at && !inv.revoked_at && !expired && sameProperty) {
      continue;
    }

    // Expired pending, or any accepted row (incl. revoked) that still blocks the server check
    if ((!inv.accepted_at && !inv.revoked_at && expired) || inv.accepted_at) {
      const { error: updErr } = await supabase
        .from("member_invites")
        .update({
          revoked_at: inv.revoked_at ?? isoNow,
          expires_at: inv.expires_at && Date.parse(inv.expires_at) <= now ? inv.expires_at : isoNow,
          accepted_at: null,
        })
        .eq("id", inv.id)
        .eq("owner_id", params.ownerId);
      if (updErr) {
        console.warn("[prepareMemberInviteSlot] clear failed", inv.id, updErr.message);
      }
    }
  }
}

/**
 * Create a member invite (owner or company admin for cleaners only).
 */
export const useCreateMemberInvite = () => {
  const queryClient = useQueryClient();

  return useMutation<CreateMemberInviteResult, Error, CreateMemberInviteParams>({
    mutationFn: async ({
      ownerId,
      email,
      inviteRole,
      propertyId = null,
      companyId = null,
      expiresDays = 7,
      accessLevel = "limited",
      permissions = null,
      propertyIds = null,
    }) => {
      const normalized = email.trim().toLowerCase();
      await prepareMemberInviteSlot({
        ownerId,
        email: normalized,
        propertyId,
      });

      const { data, error } = await supabase.rpc("create_member_invite", {
        p_owner_id: ownerId,
        p_email: normalized,
        p_invite_role: inviteRole,
        p_property_id: propertyId,
        p_company_id: companyId,
        p_expires_days: expiresDays,
        p_access_level: accessLevel ?? "limited",
        p_permissions: permissions,
        p_property_ids: propertyIds?.length ? propertyIds : null,
      });

      if (error) throw new Error(error.message);
      if (!data) throw new Error("No data returned");

      return data as CreateMemberInviteResult;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["member-invites", variables.ownerId] });
      queryClient.invalidateQueries({ queryKey: ["property-access-and-invites"] });
      if (variables.companyId) {
        queryClient.invalidateQueries({ queryKey: ["member-invites-by-company", variables.companyId] });
      }
      const propIds = [
        ...new Set(
          [variables.propertyId, ...(variables.propertyIds ?? [])].filter(
            (id): id is string => !!id,
          ),
        ),
      ];
      for (const id of propIds) {
        queryClient.invalidateQueries({ queryKey: ["property-access-and-invites", id] });
        queryClient.invalidateQueries({ queryKey: ["member-invites-by-property", id] });
      }
    },
  });
};

/**
 * List member invites for an owner (owner_id = current user).
 * Includes token so owner can copy invite links (/invite?token=...). RLS allows owner to read their own rows.
 */
export const useMemberInvites = (ownerId: string | undefined) => {
  return useQuery({
    queryKey: ["member-invites", ownerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("member_invites")
        .select("id, owner_id, email, invite_role, company_id, property_id, token, expires_at, accepted_at, revoked_at, created_at, access_level")
        .eq("owner_id", ownerId!)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return (data ?? []) as (Omit<MemberInviteRow, "created_by">)[];
    },
    enabled: !!ownerId,
  });
};

/**
 * Revoke a pending invite (sets revoked_at and expires_at = now()).
 * Owner or company admin (for their company invites).
 */
export const useRevokeMemberInvite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: string) => {
      const { data, error } = await supabase.rpc("revoke_member_invite", { p_invite_id: inviteId });
      if (error) throw new Error(error.message);
      return data as { id: string; email: string; revoked_at: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["member-invites"] });
      queryClient.invalidateQueries({ queryKey: ["member-invites-by-company"] });
      queryClient.invalidateQueries({ queryKey: ["property-access-and-invites"] });
    },
  });
};

export interface RemovePropertyAccessResult {
  removed_type: "invite" | "company_assignment" | "cleaner_assignment";
  ok: boolean;
  message?: string;
  logged?: boolean;
}

/**
 * Remove property access for an accepted member (owner only).
 * company_admin: unlink company from property; cleaner: remove assignments and property_access.
 * Caller should log error (console.error) and show toast with message on failure.
 */
export async function removePropertyAccess(
  propertyId: string,
  targetEmail: string
): Promise<RemovePropertyAccessResult> {
  // Prefer unified revoke (strips cohosts + assignments + invites across the owner's units).
  const unified = await (supabase as unknown as {
    rpc: (
      fn: string,
      args: Record<string, unknown>
    ) => Promise<{ data: unknown; error: { message: string } | null }>;
  }).rpc("remove_team_member_access", {
    p_property_id: propertyId,
    p_target_email: targetEmail.trim().toLowerCase(),
    p_cohost_user_id: null,
  });
  if (!unified.error && (unified.data as { ok?: boolean } | null)?.ok) {
    return {
      removed_type: "invite",
      ok: true,
      logged: true,
    };
  }

  const { data, error } = await supabase.rpc("remove_property_access", {
    p_property_id: propertyId,
    p_target_email: targetEmail.trim().toLowerCase(),
  });
  if (error) {
    console.error("[removePropertyAccess] RPC error", {
      propertyId,
      targetEmail,
      unifiedError: unified.error?.message,
      status: error.code,
      message: error.message,
      details: error.details,
    });
    throw new Error(unified.error?.message || error.message);
  }
  return data as RemovePropertyAccessResult;
}

/**
 * List member invites for a company (company_id). Used by team admin dashboard.
 * Token included so team admin can copy invite links for their company's cleaner invites.
 */
export const useMemberInvitesByCompany = (companyId: string | undefined) => {
  return useQuery({
    queryKey: ["member-invites-by-company", companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("member_invites")
        .select("id, owner_id, email, invite_role, company_id, property_id, token, expires_at, accepted_at, revoked_at, created_at, access_level")
        .eq("company_id", companyId!)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return (data ?? []) as (Omit<MemberInviteRow, "created_by">)[];
    },
    enabled: !!companyId,
  });
};

/** List member invites for a property (team member with property_access; RLS may limit to owner/can_invite). */
export const useMemberInvitesByProperty = (propertyId: string | undefined) => {
  return useQuery({
    queryKey: ["member-invites-by-property", propertyId],
    queryFn: async () => {
      if (!propertyId) return [];
      const { data, error } = await supabase
        .from("member_invites")
        .select("id, owner_id, email, invite_role, company_id, property_id, token, expires_at, accepted_at, revoked_at, created_at, access_level")
        .eq("property_id", propertyId)
        .order("created_at", { ascending: false });
      if (error) return [];
      return (data ?? []) as (Omit<MemberInviteRow, "created_by">)[];
    },
    enabled: !!propertyId,
  });
};

// =============================================================================
// RPC-based access + invites (replaces direct property_access table to avoid 404)
// =============================================================================

export interface PropertyAccessMemberRow {
  id: string;
  property_id: string;
  grantee_type: "user" | "company";
  grantee_user_id: string | null;
  grantee_company_id: string | null;
  access_level: "full" | "limited";
  created_at: string;
  display_name: string | null;
  /** Email of the user grantee (from auth); used to match accepted invites to members in Access & Team. */
  grantee_email?: string | null;
  /** From profiles (user-type members only); used for Access & Team and Assign-to dropdown. */
  first_name?: string | null;
  last_name?: string | null;
  team_name?: string | null;
  role?: string | null;
  /** When true, this team admin can open and complete assigned tasks (like a cleaner). */
  can_execute_tasks?: boolean;
  /** Profile photo URL (from SECURITY DEFINER RPC; bypasses own-row profiles RLS). */
  avatar_url?: string | null;
}

export interface PropertyAccessAndInvitesResult {
  members: PropertyAccessMemberRow[];
  invites: MemberInviteRow[];
}

/** Current user's can_execute_tasks for this property. Owner => true; team => Tasks permission or property_access flag. */
export function usePropertyCanExecuteTasks(
  propertyId: string | undefined,
  isOwner: boolean
): { canExecuteTasks: boolean | null; isLoading: boolean } {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["property-can-execute-tasks", propertyId, user?.id, isOwner],
    queryFn: async (): Promise<boolean> => {
      if (!propertyId || !user?.id) return false;
      if (isOwner) return true;
      const { data: accessRow } = await supabase.rpc("get_my_property_access_level", {
        p_property_id: propertyId,
      });
      const row = Array.isArray(accessRow) ? accessRow[0] : accessRow;
      const perms = (row as { permissions?: unknown } | null)?.permissions;
      if (Array.isArray(perms) && perms.includes("tasks")) return true;

      const { data: rpcData, error } = await supabase.rpc("get_property_access_and_invites", {
        p_property_id: propertyId,
      });
      if (error) return false;
      const raw = (rpcData ?? {}) as { members?: Array<{ grantee_user_id?: string | null; can_execute_tasks?: boolean }> };
      const members = raw.members ?? [];
      const me = members.find((m) => m.grantee_user_id === user.id);
      return !!(me && me.can_execute_tasks === true);
    },
    enabled: !!propertyId && !!user?.id && !isOwner,
  });
  if (isOwner) return { canExecuteTasks: true, isLoading: false };
  if (!propertyId || !user?.id) return { canExecuteTasks: null, isLoading: false };
  return { canExecuteTasks: data ?? null, isLoading };
}

/** Single RPC for Access & Team: members (property_access with display_name) + invites for property. No direct property_access table. */
export const usePropertyAccessAndInvites = (propertyId: string | undefined, ownerId: string | undefined) => {
  return useQuery({
    queryKey: ["property-access-and-invites", propertyId],
    queryFn: async (): Promise<PropertyAccessAndInvitesResult> => {
      if (!propertyId) return { members: [], invites: [] };
      const { data, error } = await supabase.rpc("get_property_access_and_invites", {
        p_property_id: propertyId,
      });
      if (error) throw error;
      const raw = (data ?? {}) as { members?: unknown[]; invites?: unknown[] };
      return {
        members: (raw.members ?? []) as PropertyAccessMemberRow[],
        invites: (raw.invites ?? []) as MemberInviteRow[],
      };
    },
    enabled: !!propertyId,
  });
};

/** Update access_level via RPC (no direct property_access table). */
export async function updatePropertyAccessLevel(
  propertyId: string,
  id: string,
  accessLevel: "full" | "limited"
): Promise<{ ok: boolean }> {
  const { data, error } = await supabase.rpc("update_property_access_level", {
    p_property_id: propertyId,
    p_id: id,
    p_access_level: accessLevel,
  });
  if (error) throw new Error(error.message);
  return (data ?? { ok: true }) as { ok: boolean };
}

/** Update can_execute_tasks for a property_access row (team admins only). */
export async function updatePropertyAccessCanExecuteTasks(
  propertyId: string,
  id: string,
  canExecuteTasks: boolean
): Promise<{ ok: boolean }> {
  const { data, error } = await supabase.rpc("update_property_access_can_execute_tasks", {
    p_property_id: propertyId,
    p_id: id,
    p_can_execute_tasks: canExecuteTasks,
  });
  if (error) throw new Error(error.message);
  return (data ?? { ok: true }) as { ok: boolean };
}

/** Update access_level for a pending invite (owner only). */
export async function updateMemberInviteAccess(
  inviteId: string,
  accessLevel: "full" | "limited"
): Promise<{ ok: boolean }> {
  const { data, error } = await supabase.rpc("update_member_invite_access", {
    p_invite_id: inviteId,
    p_access_level: accessLevel,
  });
  if (error) throw new Error(error.message);
  return (data ?? { ok: true }) as { ok: boolean };
}

/** Slice 22: update module permissions on a pending Team member invite. */
export async function updateMemberInvitePermissions(
  inviteId: string,
  permissions: string[]
): Promise<void> {
  const { error } = await supabase.rpc("update_member_invite_permissions", {
    p_invite_id: inviteId,
    p_permissions: permissions,
  });
  if (error) throw new Error(error.message);
}

export type PropertyTeamPermission = "cleaner" | "limited" | "full";

/** Switch accepted member between cleaner and co-host (limited/full). */
export async function setPropertyTeamPermission(
  propertyId: string,
  targetEmail: string,
  permission: PropertyTeamPermission
): Promise<{ ok: boolean }> {
  const { data, error } = await supabase.rpc("set_property_team_permission", {
    p_property_id: propertyId,
    p_target_email: targetEmail.trim(),
    p_permission: permission,
  });
  if (error) throw new Error(error.message);
  return (data ?? { ok: true }) as { ok: boolean };
}

export function inviteToTeamPermission(inv: {
  invite_role: string;
  access_level: string | null;
}): PropertyTeamPermission {
  if (inv.invite_role === "cleaner") return "cleaner";
  return inv.access_level === "full" ? "full" : "limited";
}

export interface ResendRevokedMemberInviteResult {
  ok: boolean;
  id: string;
  token: string;
  expires_at: string;
  email: string;
}

/** Resend a revoked invite (reuse row, new token). */
export const useResendRevokedMemberInvite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: string): Promise<ResendRevokedMemberInviteResult> => {
      const { data, error } = await supabase.rpc("resend_revoked_member_invite", { p_invite_id: inviteId });
      if (error) throw new Error(error.message);
      if (!data) throw new Error("No data returned");
      return data as ResendRevokedMemberInviteResult;
    },
    onSuccess: (_, inviteId) => {
      queryClient.invalidateQueries({ queryKey: ["member-invites"] });
      queryClient.invalidateQueries({ queryKey: ["member-invites-by-company"] });
      queryClient.invalidateQueries({ queryKey: ["property-access-and-invites"] });
    },
  });
};

/** Remove a revoked invite from the list (delete row). Owner or team admin with property access. */
export const useDeleteRevokedMemberInvite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: string) => {
      const { data, error } = await supabase.rpc("delete_revoked_member_invite", { p_invite_id: inviteId });
      if (error) throw new Error(error.message);
      return data as { ok: boolean; id: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["member-invites"] });
      queryClient.invalidateQueries({ queryKey: ["member-invites-by-company"] });
      queryClient.invalidateQueries({ queryKey: ["property-access-and-invites"] });
    },
  });
};
